/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { KeycloakService } from './core/services/keycloak-service';
import { VestaKeycloakTokenParsed } from './user/model/vesta-keycloak-token-parsed';
import { EnvironmentService } from './core/services/environment.service';
import { HeaderTokenService } from '@msi/commandcentral-user-authentication';
import { jwtDecode } from 'jwt-decode';
import { IdmTokenParsed } from './user/model/IdmTokenParsed';
import { CCAdminProxyService } from './core/services/cc-admin-proxy-service';
import { Consts } from './usb/shared/util/consts';
import { ConfirmAuthGuardService } from '@msi/commandcentral-common-header';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard {
    private static readonly ALLOWED_CLIENT_PERMISSIONS = ['call-handling-ui', 'call-handling-ui-tech'];
    // Used by the keycloak spi to filter applicable roles.
    private static readonly KEYCLOAK_SPI_ROLE_HINT = 'CALLTAKING';
    // delimiter separating application part and browser identifier
    private static readonly LOGIN_HINT_DELIMITER = "__";

    constructor(
        public router: Router,
        private readonly keycloakService: KeycloakService,
        private env: EnvironmentService,
        private preventInfiniteSpinnerRaceInjection: HeaderTokenService,
        private authenticationService: ConfirmAuthGuardService,
        private ccAdminProxyService: CCAdminProxyService
    ) {}

    public async canAccess(): Promise<boolean> {
        const keycloakActivated = await this.isKeycloakAccessAllowed();
        console.log(`keycloak is activated: ${keycloakActivated}`);
        this.logAgencyIds();
        if (!keycloakActivated) {
            return false;
        }
        if (this.env.environment.featureFlags && this.env.environment.featureFlags.ccAdminIntegrated) {
            if (await this.shouldSkipIdmAuth()) {
                console.log('Offline mode or pending reload after offline→online — skipping IDM authentication, relying on Keycloak only');
                return this.checkPermission();
            }
            return this.isAdminAuthenticated();
        }
        return this.checkPermission();
    }

    private async shouldSkipIdmAuth(): Promise<boolean> {
        if (this.env.environment.IS_CLOUD_ENVIRONMENT) {
            return false;
        }
        if (this.ccAdminProxyService.requiresReload$.value) {
            return true;
        }
        try {
            const isOnline = await this.ccAdminProxyService.waitForInitialStatus();
            return !isOnline;
        } catch {
            return true;
        }
    }

    private getEncodedBrowserIdentifierWithDelimiter(): string {
        let browserIdentifier = localStorage.getItem(Consts.BROWSER_IDENTIFIER);
        if (browserIdentifier) {
            return AuthGuard.LOGIN_HINT_DELIMITER + btoa(browserIdentifier);
        } else {
            return '';
        }
    }

    private async isKeycloakAccessAllowed(): Promise<boolean> {
        const authenticated = this.keycloakService.keycloak?.authenticated;

        if (!authenticated) {
            await this.keycloakService.keycloak.login({
                redirectUri: window.location.href,
                loginHint: AuthGuard.KEYCLOAK_SPI_ROLE_HINT + this.getEncodedBrowserIdentifierWithDelimiter()
            });
        }

        return true;
    }

    private async isAdminAuthenticated(): Promise<boolean> {
        const isAuthenticated = await this.authenticationService.isAuthenticated();

        if (!isAuthenticated) {
            console.log('Failed to authenticate');
            return false;
        }

        console.log('Admin authenticated');
        return this.checkPermission();
    }

    private checkPermission() {
        const parsedToken = this.keycloakService.keycloak.tokenParsed as VestaKeycloakTokenParsed;
        const permissions = parsedToken?.preferred_role_permissions;

        if (!permissions?.some((permission) => AuthGuard.ALLOWED_CLIENT_PERMISSIONS.includes(permission))) {
            this.router.navigate(['/unauthorized']);
            return false;
        }

        return true;
    }

    private logAgencyIds(): void {
        try {
            const keycloakToken = this.keycloakService.keycloak.tokenParsed as VestaKeycloakTokenParsed;
            const idmToken = this.preventInfiniteSpinnerRaceInjection.getToken();
            idmToken.then(
                (token : string) => {
                    if (token != null) {
                        const decodedIDMToken = jwtDecode<IdmTokenParsed>(token);
                        console.log('Keycloak: agencyId id: ' + keycloakToken.agency_fqdn +
                            ' user id: ' + keycloakToken.email + ' . IDM agency id: ' + decodedIDMToken.agency + ' user id: '+ decodedIDMToken.uid);
                    }
                }
            );
        } catch (exception) {
            console.log('Failed to fetch keycloak/idm token');
        }
    }
}

